/**
 * Planes comerciales y sus prestaciones (spec §51–§52).
 *
 * Un plan concede *features*. Los objetivos del software exigen features.
 * Los planes son acumulativos: Business incluye todo lo de Starter, etc.
 */

export const FEATURES = [
  'core',                 // cotizaciones, facturas, pagos, recibos, trazabilidad, auditoría
  'templates_basic',
  'ai_basic',             // redacción de descripciones, comandos en lenguaje natural
  'multi_company',
  'crm',
  'contracts',
  'clause_library',
  'expiries',             // alertas de licencias, visas, pasaportes
  'ocr',                  // tickets, documentos escaneados
  'multicurrency',
  'reports',
  'recurring',
  'migration',
  'mobile',
  'intl_trade',           // LOI, ICPO, SCO, FCO, incoterms, puertos
  'kyc',
  'dealroom',
  'esignature',
  'automations',
  'approvals',
  'purchasing',
  'projects',
  'portal',
  'api',
  'roles_advanced',
  'ai_advanced',          // revisión contractual, riesgos, conciliación, obligaciones
  'whitelabel',
  'sso',
  'security_advanced',
  'custom_integrations',
  'dedicated_support',
] as const;
export type Feature = (typeof FEATURES)[number];

export const PLAN_CODES = ['starter', 'business', 'professional', 'enterprise'] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

export interface PlanLimits {
  /** -1 = sin límite */
  companies: number;
  users: number;
  documentsPerMonth: number;
  storageMb: number;
  aiCreditsPerMonth: number;
  ocrPagesPerMonth: number;
}

export interface Plan {
  code: PlanCode;
  name: string;
  tagline: string;
  /** Features propias del plan (sin heredar). */
  ownFeatures: Feature[];
  limits: PlanLimits;
  order: number;
}

const PLAN_DEFS: Plan[] = [
  {
    code: 'starter',
    name: 'Starter',
    tagline: 'Una empresa, el ciclo comercial completo.',
    order: 1,
    ownFeatures: ['core', 'templates_basic', 'ai_basic'],
    limits: {
      companies: 1, users: 3, documentsPerMonth: 50,
      storageMb: 1024, aiCreditsPerMonth: 100, ocrPagesPerMonth: 20,
    },
  },
  {
    code: 'business',
    name: 'Business',
    tagline: 'Grupo de empresas, contratos, LOI, vencimientos y OCR.',
    order: 2,
    ownFeatures: [
      'multi_company', 'crm', 'contracts', 'clause_library', 'expiries',
      'ocr', 'multicurrency', 'reports', 'recurring', 'migration', 'mobile', 'intl_trade',
    ],
    limits: {
      companies: 5, users: 15, documentsPerMonth: 500,
      storageMb: 25_600, aiCreditsPerMonth: 1_000, ocrPagesPerMonth: 500,
    },
  },
  {
    code: 'professional',
    name: 'Professional',
    tagline: 'KYC, Deal Room, firma, aprobaciones, automatizaciones y API.',
    order: 3,
    ownFeatures: [
      'kyc', 'dealroom', 'esignature', 'automations', 'approvals',
      'purchasing', 'projects', 'portal', 'api', 'roles_advanced', 'ai_advanced',
    ],
    limits: {
      companies: 20, users: 50, documentsPerMonth: 5_000,
      storageMb: 204_800, aiCreditsPerMonth: 10_000, ocrPagesPerMonth: 5_000,
    },
  },
  {
    code: 'enterprise',
    name: 'Enterprise',
    tagline: 'Marca propia, SSO, integraciones y región dedicada.',
    order: 4,
    ownFeatures: [
      'whitelabel', 'sso', 'security_advanced', 'custom_integrations', 'dedicated_support',
    ],
    limits: {
      companies: -1, users: -1, documentsPerMonth: -1,
      storageMb: -1, aiCreditsPerMonth: -1, ocrPagesPerMonth: -1,
    },
  },
];

export const PLANS: Record<PlanCode, Plan> = Object.fromEntries(
  PLAN_DEFS.map((p) => [p.code, p]),
) as Record<PlanCode, Plan>;

/** Features efectivas de un plan, incluyendo las heredadas de planes inferiores. */
export function featuresOfPlan(code: PlanCode): Set<Feature> {
  const target = PLANS[code];
  if (!target) return new Set(['core']);
  const set = new Set<Feature>();
  for (const plan of PLAN_DEFS) {
    if (plan.order <= target.order) plan.ownFeatures.forEach((f) => set.add(f));
  }
  return set;
}

export function limitsOfPlan(code: PlanCode): PlanLimits {
  return PLANS[code]?.limits ?? PLANS.starter.limits;
}

/** Plan mínimo que incluye una feature — sirve para el aviso "disponible en…". */
export function minimumPlanFor(feature: Feature): PlanCode {
  const plan = PLAN_DEFS.find((p) => p.ownFeatures.includes(feature));
  return plan?.code ?? 'starter';
}

export function planIncludes(code: PlanCode, feature: Feature): boolean {
  return featuresOfPlan(code).has(feature);
}

export function isUnlimited(limit: number): boolean {
  return limit < 0;
}

/** ¿El uso actual excede el límite del plan? */
export function exceedsLimit(used: number, limit: number): boolean {
  return !isUnlimited(limit) && used >= limit;
}
